import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import adminService from '../../../services/adminService';
import { Plus } from 'lucide-react';

const ActiveUsersTable = ({ onSuspend, onViewActivity, onUserClick, onAddCredits, searchQuery = '' }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const usersData = await adminService?.getAllActiveUsers();
      setUsers(usersData || []);
    } catch (err) {
      console.error('Error loading users:', err);
      setError(err?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: { class: 'status-badge-success', icon: 'CheckCircle2', text: 'Active' },
      suspended: { class: 'status-badge-error', icon: 'XCircle', text: 'Suspended' },
      trial: { class: 'status-badge-warning', icon: 'Clock', text: 'Inactive' }
    };
    return badges?.[status] || badges?.active;
  };

  const getSubscriptionBadge = (tier) => {
    const colors = {
      'Free': 'bg-gray-100 text-gray-700',
      'Starter': 'bg-blue-100 text-blue-700',
      'Professional': 'bg-purple-100 text-purple-700',
      'Enterprise': 'bg-green-100 text-green-700'
    };
    return colors?.[tier] || colors?.Free;
  };

  // Filter users based on search query
  const filteredUsers = users?.filter((user) => {
    if (!searchQuery) return true;
    
    const query = searchQuery?.toLowerCase();
    return (
      user?.name?.toLowerCase()?.includes(query) ||
      user?.email?.toLowerCase()?.includes(query) ||
      user?.specialty?.toLowerCase()?.includes(query) ||
      user?.institution?.toLowerCase()?.includes(query)
    );
  });

  if (loading) {
    return (
      <div className="p-12 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p className="text-gray-500">Loading users...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
          <button
            onClick={loadUsers}
            className="mt-2 text-red-600 hover:text-red-800 underline text-sm"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (users?.length === 0) {
    return (
      <div className="p-12 text-center">
        <Icon name="Users" size={48} color="#9CA3AF" className="mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No active users found</h3>
        <p className="text-gray-500">Users will appear here once they register and are verified.</p>
      </div>
    );
  }

  if (filteredUsers?.length === 0 && searchQuery) {
    return (
      <div className="p-12 text-center">
        <Icon name="Search" size={48} color="#9CA3AF" className="mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No users found</h3>
        <p className="text-gray-500">Try adjusting your search query</p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                User
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Specialty
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Institution
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Subscription
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Credits
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Last Active
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredUsers?.map((user) => {
              const statusBadge = getStatusBadge(user?.status);
              return (
                <tr 
                  key={user?.id} 
                  className="hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => onUserClick?.(user)}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-3">
                      <Image
                        src={user?.profileImage}
                        alt={user?.profileImageAlt}
                        className="w-10 h-10 rounded-lg object-cover"
                      />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                        <p className="text-xs text-gray-500">{user?.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">{user?.specialty}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">{user?.institution}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSubscriptionBadge(user?.subscriptionTier)}`}>
                      {user?.subscriptionTier}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <Icon name="Coins" size={14} color="#8B5CF6" />
                      <span className="text-sm font-mono text-gray-900">{user?.creditBalance}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium gap-1 ${
                      statusBadge?.class === 'status-badge-success' ? 'bg-green-100 text-green-800' :
                      statusBadge?.class === 'status-badge-error'? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      <Icon name={statusBadge?.icon} size={12} />
                      {statusBadge?.text}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-500">{user?.lastActive}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e?.stopPropagation();
                          onAddCredits?.(user);
                        }}
                        className="p-1.5 rounded hover:bg-green-50 transition-colors"
                        title="Add Credits"
                      >
                        <Plus size={16} color="#10B981" strokeWidth={2.5} />
                      </button>
                      <button
                        onClick={(e) => {
                          e?.stopPropagation();
                          onViewActivity?.(user?.id);
                        }}
                        className="p-1.5 rounded hover:bg-gray-100 transition-colors"
                        title="View Activity"
                      >
                        <Icon name="Activity" size={16} color="#3B82F6" />
                      </button>
                      <button
                        onClick={(e) => {
                          e?.stopPropagation();
                          onSuspend?.(user?.id);
                        }}
                        className="p-1.5 rounded hover:bg-red-50 transition-colors"
                        title="Suspend User"
                        disabled={user?.status === 'suspended'}
                      >
                        <Icon name="Ban" size={16} color={user?.status === 'suspended' ? '#9CA3AF' : '#EF4444'} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ActiveUsersTable;